import type { ProductBacklogSection } from '@cortex/domain-ac-mens-hub-api';
import { ChevronDown } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { NEXT_ASSIGNMENT_CANDIDATES } from '../../lib/product-backlog-view.js';
import { ProductBacklogCard } from './ProductBacklogCard.js';

/**
 * 区分の見出しの部品（名前と件数）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の区分の見出しに、区分の名前と件数を出す
 * @graph-connects none
 */
function ShelfTitle({ section }: { section: ProductBacklogSection }) {
  const { t } = useTranslation();
  return (
    <>
      <span className="text-sm font-semibold text-[var(--color-text-primary)]">
        {t(`productBacklog.sections.${section.kind}.name`)}
      </span>
      <span className="rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 text-xs font-medium tabular-nums text-[var(--color-text-secondary)]">
        {t('productBacklog.count', { n: String(section.cards.length) })}
      </span>
    </>
  );
}

/**
 * 区分1つ（棚）。並べる区分は常に開いて意味の1行を添え、0件でも「0件」を出す。要件定義アサイン希望は
 * 上位5件の下に「ここまでが次のアサイン候補」の線を引く。見るだけの区分は見出しを押して開け閉めする。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の区分1つを、見出し（名前・件数・意味）と札の並びで出す。見るだけの区分は畳んで件数だけを出し、要件定義アサイン希望は上位5件の下に次のアサイン候補の線を引く
 * @graph-connects ShelfTitle [calls] 区分の名前と件数
 * @graph-connects ProductBacklogCard [calls] 札
 */
export function ProductBacklogShelf({
  section,
  backlogStatus,
  focusKey,
  collapsible,
  open,
  onToggle,
}: {
  section: ProductBacklogSection;
  backlogStatus: 'ok' | 'unavailable';
  focusKey: string | null;
  collapsible: boolean;
  open: boolean;
  onToggle?: () => void;
}) {
  const { t } = useTranslation();
  const showCut =
    section.kind === 'req_waiting' && section.cards.length > NEXT_ASSIGNMENT_CANDIDATES;
  const nameId = `product-backlog-shelf-${section.kind}-${String(section.separatorRowNumber)}`;

  return (
    <section
      aria-labelledby={nameId}
      className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]"
    >
      {collapsible ? (
        <button
          type="button"
          id={nameId}
          aria-expanded={open}
          onClick={onToggle}
          className="flex w-full items-center gap-2 bg-[var(--color-bg-elevated)] px-3 py-2 text-left"
        >
          <ChevronDown
            className={`h-4 w-4 text-[var(--color-text-muted)] transition-transform ${open ? '' : '-rotate-90'}`}
            aria-hidden="true"
          />
          <ShelfTitle section={section} />
        </button>
      ) : (
        <div
          id={nameId}
          className="flex w-full flex-wrap items-center gap-2 bg-[var(--color-bg-elevated)] px-3 py-2"
        >
          <ShelfTitle section={section} />
          <span className="text-xs text-[var(--color-text-muted)]">
            {t(`productBacklog.sections.${section.kind}.glossary`)}
          </span>
        </div>
      )}
      {open && (
        <div className="min-h-2">
          {section.cards.length === 0 ? (
            <div className="p-4 text-center text-[13px] text-[var(--color-text-muted)]">
              {t('productBacklog.empty')}
            </div>
          ) : (
            section.cards.map((card, index) => (
              <Fragment key={card.rowNumber}>
                <ProductBacklogCard
                  card={card}
                  backlogStatus={backlogStatus}
                  focused={focusKey !== null && card.key === focusKey}
                />
                {showCut && index === NEXT_ASSIGNMENT_CANDIDATES - 1 && (
                  <div
                    role="separator"
                    className="border-y border-dashed border-[var(--color-accent-primary)]/30 bg-[var(--color-accent-muted)] px-3.5 py-1 text-[11.5px] font-semibold text-[var(--color-accent-primary)]"
                  >
                    {t('productBacklog.nextCandidates')}
                  </div>
                )}
              </Fragment>
            ))
          )}
        </div>
      )}
    </section>
  );
}
